import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { moneyExact } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/lib/auth";
import { FileSignature, Receipt, ArrowRight } from "lucide-react";

export default function PortalHome() {
  const { user } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    api.get("/estimates").then((r) => setEstimates(r.data));
    api.get("/invoices").then((r) => setInvoices(r.data));
  }, []);

  const unpaid = invoices.filter((i) => i.status !== "paid");

  return (
    <DashboardLayout role="client" title={`Welcome, ${user?.name?.split(' ')[0] || ''}`} subtitle="Your projects, estimates, and invoices in one place.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Stat label="Estimates" value={estimates.length} icon={FileSignature} to="/portal/estimates" />
        <Stat label="Invoices" value={invoices.length} icon={Receipt} to="/portal/invoices" />
        <Stat label="Unpaid balance" value={moneyExact(unpaid.reduce((a, b) => a + b.total, 0))} icon={Receipt} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Recent Estimates</h2>
            <Link to="/portal/estimates" className="text-sm flex items-center gap-1" style={{color:'var(--accent)'}}>All <ArrowRight size={13}/></Link>
          </div>
          {estimates.length === 0 ? <Empty /> : (
            <div className="divide-y divide-white/5">
              {estimates.slice(0, 4).map((e) => (
                <Link key={e.id} to="/portal/estimates" className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{e.title}</div>
                    <div className="text-xs" style={{color:'var(--text-muted)'}}>{moneyExact(e.total)}</div>
                  </div>
                  <span className={`badge ${e.status === 'approved' ? 'badge-green' : ''}`}>{e.status}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold">Open Invoices</h2>
            <Link to="/portal/invoices" className="text-sm flex items-center gap-1" style={{color:'var(--accent)'}}>All <ArrowRight size={13}/></Link>
          </div>
          {unpaid.length === 0 ? <Empty msg="No open invoices. You're all caught up." /> : (
            <div className="divide-y divide-white/5">
              {unpaid.slice(0, 4).map((i) => (
                <Link key={i.id} to="/portal/invoices" className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{i.title}</div>
                    <div className="text-xs" style={{color:'var(--text-muted)'}}>Due: {i.due_date || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{moneyExact(i.total)}</div>
                    <div className="text-xs" style={{color:'var(--accent)'}}>Pay now →</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, icon: Icon, accent, to }) {
  const Inner = (
    <>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider mb-3" style={{color:'var(--text-muted)'}}>
        <Icon size={13} style={{color: accent ? 'var(--accent)' : 'var(--text-muted)'}}/> {label}
      </div>
      <div className="font-display text-3xl font-bold" style={accent ? {color:'var(--accent)'} : {}}>{value}</div>
    </>
  );
  return to ? <Link to={to} className="card p-5 block">{Inner}</Link> : <div className="card p-5">{Inner}</div>;
}

function Empty({ msg = "Nothing here yet." }) {
  return <div className="text-sm py-6 text-center" style={{color:'var(--text-muted)'}}>{msg}</div>;
}
